import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { ScrollText, ShieldCheck, ShieldAlert, Cpu } from "lucide-react";

const ACTION_META = {
  order_created: { label: "Commande créée", color: "#A1A1AA" },
  auto_delivered: { label: "Livraison AUTO", color: "#00E5A0" },
  status_changed: { label: "Statut modifié", color: "#FFB020" },
  processed_uploaded: { label: "Fichier traité", color: "#00E5FF" },
  solution_imported: { label: "Solution importée", color: "#8B5CF6" },
  payment_credited: { label: "Crédits payés", color: "#00E5A0" },
  promo_created: { label: "Code promo créé", color: "#8B5CF6" },
  promo_redeemed: { label: "Code promo utilisé", color: "#00E5A0" },
  promo_updated: { label: "Code promo modifié", color: "#FFB020" },
  promo_deleted: { label: "Code promo supprimé", color: "#FF4D4D" },
  family_checksum_toggle: { label: "Checksum famille", color: "#00E5FF" },
};

export const AdminAudit = () => {
  const [logs, setLogs] = useState([]);
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadFamilies = () => api.get("/admin/families").then(({ data }) => setFamilies(data));
  useEffect(() => {
    api.get("/admin/audit").then(({ data }) => setLogs(data)).finally(() => setLoading(false));
    loadFamilies();
  }, []);

  const toggleFamily = async (family, current) => {
    try {
      await api.post("/admin/families/toggle", { family, checksum_validated: !current });
      toast.success(`${family} : checksum ${!current ? "activé" : "désactivé"}`);
      loadFamilies();
    } catch (e) { toast.error("Action impossible"); }
  };

  return (
    <AppShell mode="admin">
      <div className="mb-8">
        <div className="font-mono text-xs text-aurora-purple tracking-[0.3em] uppercase mb-2">// Traçabilité</div>
        <h1 className="font-display text-4xl font-bold uppercase tracking-tight">Journal d'audit</h1>
      </div>

      {/* Checksum engine governance */}
      <div className="glass rounded-md p-6 mb-8">
        <h3 className="font-display text-lg font-semibold uppercase tracking-tight mb-1">Moteur checksum par famille d'ECU</h3>
        <p className="text-xs text-zinc-400 mb-5 max-w-2xl">
          La livraison automatique « niveau 2 » (original différent) reste <b className="text-signal-error">désactivée</b> tant qu'aucun moteur de checksum validé n'est disponible ET que vous ne l'avez pas explicitement activé après validation au banc, projet par projet.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {families.map((f) => (
            <div key={f.family} data-testid={`family-${f.family.replace(/\s/g, '-')}`} className="flex items-center justify-between p-3 rounded-sm border border-white/10 bg-black/30">
              <div className="min-w-0">
                <div className="font-mono text-xs text-white truncate">{f.family}</div>
                <div className="font-mono text-[9px] uppercase tracking-widest" style={{ color: f.engine_available ? "#00E5A0" : "#52525B" }}>
                  Moteur {f.engine_available ? "disponible" : "non fourni"}
                </div>
              </div>
              <button
                onClick={() => f.engine_available && toggleFamily(f.family, f.checksum_validated)}
                disabled={!f.engine_available}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm font-mono text-[10px] uppercase tracking-widest border transition-colors ${f.checksum_validated ? "border-signal-success/50 text-signal-success bg-signal-success/10" : "border-white/15 text-zinc-400"} ${!f.engine_available ? "opacity-40 cursor-not-allowed" : "hover:border-white/40"}`}
              >
                {f.checksum_validated ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldAlert className="w-3.5 h-3.5" />}
                {f.checksum_validated ? "Validé" : "OFF"}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="glass rounded-md overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 border-b border-white/10 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          <div className="col-span-2">Date</div>
          <div className="col-span-2">Action</div>
          <div className="col-span-3">Acteur</div>
          <div className="col-span-5">Détails</div>
        </div>
        {loading ? (
          <div className="font-mono text-xs text-zinc-500 py-12 text-center">Chargement...</div>
        ) : logs.length === 0 ? (
          <div className="text-center py-16">
            <ScrollText className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">Aucun événement enregistré.</p>
          </div>
        ) : (
          logs.map((l) => {
            const meta = ACTION_META[l.action] || { label: l.action, color: "#A1A1AA" };
            return (
              <div key={l.id} data-testid={`audit-row-${l.id}`} className="grid grid-cols-2 md:grid-cols-12 gap-4 px-6 py-3 border-b border-white/5 last:border-0 items-center">
                <div className="md:col-span-2 font-mono text-[10px] text-zinc-400">{(l.ts || "").slice(0, 19).replace("T", " ")}</div>
                <div className="md:col-span-2">
                  <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border" style={{ color: meta.color, borderColor: `${meta.color}55` }}>{meta.label}</span>
                </div>
                <div className="md:col-span-3 font-mono text-[11px] text-zinc-300 truncate">{l.actor}</div>
                <div className="md:col-span-5 font-mono text-[10px] text-zinc-500 truncate">
                  {l.order_id ? `#${l.order_id.slice(0, 8)} · ` : ""}{Object.entries(l.details || {}).map(([k, v]) => `${k}=${typeof v === "string" ? v.slice(0, 24) : JSON.stringify(v)}`).join(" · ")}
                </div>
              </div>
            );
          })
        )}
      </div>
    </AppShell>
  );
};
